import { FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useEffect, useRef, useState } from 'react';
import { SaveScheduleOptions, ScheduleSetting, ScheduleSettingPayload } from '../../../store/ScheduleSettings';
import { TextBlock } from '../../Atoms/Text';
import { offersExecutiveSummary } from './viewKey';
import { ReportTabsEditor } from './ReportTabsEditor';
import { initialTabs, MAX_NAME_LENGTH, PickableView, TabRow, toViewsPayload, viewNameIn } from './scheduleTabs';
import { isScheduleOwner } from './scheduleOwnership';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { SCHEDULER_SELECT_CLASS } from './schedulerFieldClasses';
import { ScheduleFormFields } from './ScheduleFormFields';
import { ScheduleSummary } from './ScheduleSummary';
import { useScheduleForm } from './useScheduleForm';
import { useSchedulePreview } from './useSchedulePreview';

export interface SchedulerInlineProps {
  viewKey: string;
  viewName: string;
  // The saved views of this page that can join the report as tabs; the view it was opened from is always one.
  availableViews?: PickableView[];
  existingSchedule: ScheduleSetting | null;
  currentUserId: number | string;
  currentUserEmail: string;
  // Scopes the schedule to one project's related inventory; omitted for account-wide views.
  projectId?: string;
  onSave: (
    payload: ScheduleSettingPayload,
    existingId?: string,
    options?: SaveScheduleOptions
  ) => Promise<ScheduleSetting | null>;
  onDelete: (id: string) => Promise<boolean>;
  onOptOut: (id: string) => Promise<ScheduleSetting | null>;
  onResume: (id: string) => Promise<ScheduleSetting | null>;
  onClose: () => void;
}

/** A saved view's schedule beside the grid: its owner edits, resumes or deletes it, and anyone it is shared with opts out. */
export const SchedulerInline = ({
  viewKey,
  viewName,
  availableViews = [],
  existingSchedule,
  currentUserId,
  currentUserEmail,
  projectId,
  onSave,
  onDelete,
  onOptOut,
  onResume,
  onClose
}: SchedulerInlineProps) => {
  const isOwn = isScheduleOwner(existingSchedule, currentUserId);
  const isShared = !!existingSchedule && !isOwn;
  const isPaused = !!existingSchedule?.paused_at;

  // A save the server only half kept still puts the schedule in the list, and the form must not be reseeded from it.
  const keepFormOnReseed = useRef(false);
  // The schedule a save the server only half kept created, so that the next Save updates it instead of creating another.
  const storedId = useRef<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(!existingSchedule);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [reportName, setReportName] = useState(existingSchedule?.name ?? viewName);
  const [reportNameEdited, setReportNameEdited] = useState(false);
  const [tabRows, setTabRows] = useState<TabRow[]>(() => initialTabs(existingSchedule, viewKey, viewName));
  const scheduleForm = useScheduleForm(existingSchedule, currentUserEmail);
  const [includeExecutiveSummary, setIncludeExecutiveSummary] = useState(false);

  useEffect(() => {
    if (keepFormOnReseed.current) {
      keepFormOnReseed.current = false;
      return;
    }
    storedId.current = undefined;

    setIsEditing(!existingSchedule);
    setShowDeleteConfirm(false);

    setReportName(existingSchedule?.name ?? viewName);
    setReportNameEdited(false);
    setTabRows(initialTabs(existingSchedule, viewKey, viewName));

    scheduleForm.reseed();
    setIncludeExecutiveSummary(existingSchedule?.include_executive_summary ?? false);
  }, [existingSchedule]);

  // Until Edit, preview the stored rule: it is what the runner sends, and the form can't express every rule.
  const preview = useSchedulePreview(
    existingSchedule && !isEditing ? existingSchedule.schedule_rule : scheduleForm.rebuiltRule,
    scheduleForm.endDateValue
  );

  const viewNameOf = (key: string) => viewNameIn(availableViews, key);
  // A new report is named after its first tab until the user names it.
  const shownReportName = existingSchedule || reportNameEdited ? reportName : viewNameOf(tabRows[0].viewKey);

  const buildPayload = (): ScheduleSettingPayload => ({
    name: shownReportName.trim() || viewNameOf(tabRows[0].viewKey),
    ...scheduleForm.scheduleFields(),
    include_executive_summary: includeExecutiveSummary,
    view_key: tabRows[0].viewKey,
    views: toViewsPayload(tabRows, viewNameOf),
    ...(projectId ? { project_id: projectId } : {})
  });

  const handleSave = async () => {
    setSaving(true);
    keepFormOnReseed.current = true;
    const payload = buildPayload();
    const saved = await onSave(payload, storedId.current ?? existingSchedule?.id, {
      onStored: (schedule) => {
        storedId.current = schedule.id;
      }
    });
    if (saved) {
      keepFormOnReseed.current = false;
      onClose();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!existingSchedule) return;
    setSaving(true);
    const success = await onDelete(existingSchedule.id);
    if (success) onClose();
    setSaving(false);
  };

  const handleOptOut = async () => {
    if (!existingSchedule) return;
    setSaving(true);
    const success = await onOptOut(existingSchedule.id);
    if (success) onClose();
    setSaving(false);
  };

  const handleResume = async () => {
    if (!existingSchedule) return;
    setSaving(true);
    const resumed = await onResume(existingSchedule.id);
    if (resumed) onClose();
    setSaving(false);
  };

  const disabled = !isEditing || isShared;
  const dimmed = disabled ? { opacity: 0.5, pointerEvents: 'none' as const } : {};

  return (
    <div className="border border-gray-200 rounded-md p-4 mt-1 mb-1 bg-white relative text-sm">
      {/* Header actions */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {isOwn && !isEditing && (
          <>
            <Tooltip title="Edit schedule">
              <IconButton
                size="small"
                sx={{ color: 'rgb(107 114 128)' }}
                onClick={() => setIsEditing(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete schedule">
              <IconButton
                size="small"
                sx={{ color: 'rgb(107 114 128)' }}
                onClick={() => setShowDeleteConfirm(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {isShared && (
          <button
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            onClick={handleOptOut}
            disabled={saving}>
            <span>Opt-out from receiving</span>
            <HighlightOffIcon
              fontSize="small"
              sx={{ color: 'rgb(107 114 128)' }}
            />
          </button>
        )}
      </div>

      {/* Outside the form wrapper on purpose: that wrapper drops pointer events, which would make Resume inert. */}
      {isPaused && !showDeleteConfirm && (
        // The header cluster above is absolute, so the notice reserves room for it — as much as it takes.
        <div className={`mb-3 ${isShared ? 'pr-40' : 'pr-24'}`}>
          <TextBlock
            size="sm"
            weight="medium"
            color="grey9">
            This schedule is paused and will not send until it is resumed.
          </TextBlock>
          {existingSchedule?.paused_reason && (
            <TextBlock
              size="xs"
              color="grey6"
              className="mt-1">
              {existingSchedule.paused_reason}
            </TextBlock>
          )}
          {isOwn && (
            <button
              className={`${DEFAULT_ADVANCE_BUTTON_STYLE} mt-2`}
              onClick={handleResume}
              disabled={saving}>
              Resume schedule
            </button>
          )}
        </div>
      )}

      {showDeleteConfirm ? (
        <div className="py-2">
          <p className="text-sm text-gray-700 mb-3">Are you sure you want to delete this recurring schedule?</p>
          <div className="flex gap-2">
            <button
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              onClick={handleDelete}
              disabled={saving}>
              Yes, delete
            </button>
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={dimmed}>
            <div className="mb-3">
              <label
                htmlFor={`report-name-${viewKey}`}
                className="block text-xs font-medium text-gray-600 mb-1">
                Report Name
              </label>
              <input
                id={`report-name-${viewKey}`}
                type="text"
                className={`${SCHEDULER_SELECT_CLASS} w-full`}
                value={shownReportName}
                maxLength={MAX_NAME_LENGTH}
                disabled={disabled}
                onChange={(e) => {
                  setReportName(e.target.value);
                  setReportNameEdited(true);
                }}
              />
            </div>
            <ReportTabsEditor
              rows={tabRows}
              views={availableViews}
              disabled={disabled}
              onChange={setTabRows}
            />
            <ScheduleFormFields
              viewKey={viewKey}
              scheduleForm={scheduleForm}
              disabled={disabled}
            />
            {offersExecutiveSummary(viewKey) && (
              <div className="flex flex-col items-start">
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Switch
                      size="small"
                      checked={includeExecutiveSummary}
                      disabled={disabled}
                      onChange={(e) => setIncludeExecutiveSummary(e.target.checked)}
                    />
                  }
                  label={<span className="text-sm">Include Executive Summary</span>}
                />
              </div>
            )}
          </div>

          {/* Outside the dimmed wrapper, as a saved schedule opens read-only and its next runs are read there. */}
          <ScheduleSummary
            form={scheduleForm.form}
            preview={preview}
            paused={isPaused}
            hasEndDate={scheduleForm.endDateValue !== null}
          />

          {/* Actions */}
          <div
            className="flex justify-between items-center mt-2"
            style={dimmed}>
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={onClose}
              disabled={saving}>
              Cancel
            </button>
            <button
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              onClick={handleSave}
              disabled={disabled || saving || !scheduleForm.datesValid || scheduleForm.notAdded.length > 0}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
